import React, { useContext, useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate, useMatch, useOutletContext, useSearchParams } from 'react-router-dom'
import {
  Filter as FilterIcon,
  Save,
  ClipboardList,
  MapPin,
} from 'lucide-react'

import {
  OrderActionsProvider,
  useOrderActions,
} from '../../../contexts/OrderActionsContext'

import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import { PageHeader } from '../../../components/StandardPage'
import { Header } from '../../../components/SubHeaderFilter/types'

import { buildSteelOrdersTableHeaders, steelOrdersTableHeaders } from './SteelOrdersTable/utils'
import { getOrderColumns } from './SteelOrdersTable/api'
import ConfirmationModal from '../../../components/ModalConfirmation'
import {
  Tab,
  TabsDiv,
} from '../../../components/styles/subheader-style'
import { useSearch } from '../../../contexts/SearchContext'
import ModalContext from '../../../contexts/ModalContext'
import { FeedbackContext } from '../../../contexts/FeedbackContext'
import ApiService from '../../../services/ApiService'

import { Content, SectionName } from './style'
import { Button } from './AddOrder/style'
import ModuleKanban from '../Pipelines/ModuleKanban'

import { usePermissions } from '../../../hooks/usePermissions'

const TAB_INDEX_BUDGET = 0
const TAB_INDEX_TRACKING = 1
const TAB_INDEX_BOARD = 2

const steelTabs = [
  {
    path: '/panel/steel',
    name: 'Orçamentos',
    permission: 'orcamentos',
    index: TAB_INDEX_BUDGET,
  },
  {
    path: '/panel/steel/tracking',
    name: 'Rastreio',
    permission: 'rastreio',
    index: TAB_INDEX_TRACKING,
  },
  {
    path: '/panel/steel/board',
    name: 'Funil',
    permission: 'funil',
    index: TAB_INDEX_BOARD,
  },
]

const steelTabIcon = (index: number) =>
  index === TAB_INDEX_BUDGET ? (
    <ClipboardList size={12.9} style={{ marginRight: 7 }} />
  ) : index === TAB_INDEX_TRACKING ? (
    <MapPin size={12.8} style={{ marginRight: 7 }} />
  ) : (
    <FilterIcon size={12.8} style={{ marginRight: 7 }} />
  )

const SteelOrdersLayout: React.FC = () => {
  const { clearSearch } = useSearch()
  const navigate = useNavigate()
  const location = useLocation()
  const trackingMatch = useMatch('/panel/steel/tracking')
  const boardMatch = useMatch('/panel/steel/board')
  const { triggerSaveOrder, isSavingOrder } = useOrderActions()
  const parentContext = useOutletContext<{ search: string }>();
  const search = parentContext?.search || "";
  const { hasPermission } = usePermissions()

  const [refresh, setRefresh] = useState(false)
  const [dynamicFilterHeaders, setDynamicFilterHeaders] = useState<Header[]>(steelOrdersTableHeaders)

  useEffect(() => {
    getOrderColumns()
      .then((cols) => {
        if (cols && cols.length > 0) {
          setDynamicFilterHeaders(buildSteelOrdersTableHeaders(cols))
        }
      })
      .catch(() => {
        setDynamicFilterHeaders(steelOrdersTableHeaders)
      })
  }, [])

  const {
    appliedFilters,
    handleApplyFilter,
    handleRemoveFilter,
    handleClearAll
  } = usePersistedFilters('steel_orders')
  const [showCancelModal, setShowCancelModal] = useState(false)

  const isCreatePage = location.pathname.includes('/panel/steel/create')
  const isEditPage = location.pathname.includes('/panel/steel/edit')
  const tabIndex = boardMatch ? TAB_INDEX_BOARD : (trackingMatch ? TAB_INDEX_TRACKING : TAB_INDEX_BUDGET)
  const isFormPage = isCreatePage || isEditPage
  const isBoardTab = tabIndex === TAB_INDEX_BOARD
  const newOrderId = location.state?.newOrderId

  const token = 
  document.cookie.split('; ').find(row => row.startsWith('token='))?.split('=')[1] || 
  localStorage.getItem('token') || 
  localStorage.getItem('token');
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const faturarOrderId = Number(searchParams.get('faturar')) || null
  const { setSelectedSteelOrder, setModalSection, setModalActive } = useContext(ModalContext)
  const { setFeedback } = useContext(FeedbackContext)

  useEffect(() => {
    if (!faturarOrderId) return
    navigate('/panel/steel', { replace: true })
    ApiService.steelOrders
      .getOrder(faturarOrderId)
      .then(({ data }) => {
        setSelectedSteelOrder({
          ...data,
          client: data.client ?? data.Company_Order_clientIdToCompany?.companyName ?? null,
        })
        setModalSection('generateOrderInvoice')
        setModalActive(true)
      })
      .catch(() => {
        setFeedback({ message: 'Não foi possível abrir o faturamento do pedido.', isOpen: true, statusOk: false })
      })
  }, [faturarOrderId, navigate, setSelectedSteelOrder, setModalSection, setModalActive, setFeedback])
  const handleCancel = () => {
    setShowCancelModal(false)
    if (returnTo) {
      navigate(returnTo, { replace: true })
    } else {
      navigate('/panel/steel')
    }
  }

  let userRole = '';
  if (token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    const decoded = JSON.parse(jsonPayload);
    userRole = decoded.role;
  } catch (e) {
    console.error("❌ Erro ao decodificar token no SubHeader", e);
  }
} else {
  console.warn("⚠️ Token não encontrado no SubHeader!");
}

  const isTenantClient = userRole === 'TENANT_CLIENT';

  useEffect(() => {
  if (isTenantClient && location.pathname === '/panel/steel') {
    navigate('/panel/steel/tracking', { replace: true });
  }
}, [isTenantClient, location.pathname, navigate]);

  const visibleTabs = steelTabs.filter(
    (tab) => !(isTenantClient && tab.index === TAB_INDEX_BUDGET) && hasPermission(tab.permission),
  )

  return (
    <>
    {!isBoardTab && (
      <PageHeader
        tableName="Orders"
        moduleName="Vendas Aço"
        title={
          isFormPage
            ? isEditPage
              ? 'Editar Pedido'
              : `Nova Proposta/Pedido: ${newOrderId || ''}`
            : 'Vendas aço'
        }
        tabs={
          !isFormPage
            ? visibleTabs.map((tab) => ({
                key: tab.path,
                label: tab.name,
                icon: steelTabIcon(tab.index),
              }))
            : undefined
        }
        activeTab={steelTabs[tabIndex].path}
        onTabChange={(key) => navigate(key)}
        onReload={
          !isFormPage && !isTenantClient
            ? () => setRefresh((prev) => !prev)
            : undefined
        }
        filterHeaders={
          !isFormPage && !isTenantClient ? dynamicFilterHeaders : undefined
        }
        appliedFilters={appliedFilters}
        onApplyFilter={handleApplyFilter}
        onRemoveFilter={handleRemoveFilter}
        onClearAllFilters={handleClearAll}
        extraActions={
          isFormPage ? (
            <>
              <Button
                variant='danger-outline'
                onClick={() => setShowCancelModal(true)}
              >
                Cancelar
              </Button>
              <Button variant='primary' onClick={triggerSaveOrder} disabled={isSavingOrder}>
                <Save size={18} style={{ marginRight: '8px' }} />
                {isSavingOrder ? 'Salvando...' : isEditPage ? 'Salvar Alterações' : 'Salvar Pedido'}
              </Button>
            </>
          ) : undefined
        }
        createButton={
          !isFormPage && !isTenantClient
            ? {
                label: 'Nova proposta',
                onClick: () => navigate('/panel/steel/create'),
              }
            : undefined
        }
      />
    )}

    {isBoardTab ? (
      <ModuleKanban
        moduleType="sales"
        moduleName="Vendas Aço"
        subModule="steel"
        headerLeftContent={
          <>
            <SectionName>
              Vendas aço
            </SectionName>
            <TabsDiv>
              {visibleTabs.map((tab) => (
                  <Tab
                    key={tab.index}
                    $isFirst={tab.index === 0}
                    $isLast={tab.index === steelTabs.length - 1}
                    $isActive={tab.index === tabIndex}
                    onClick={() => {
                      navigate(tab.path)
                    }}
                  >
                    {steelTabIcon(tab.index)}
                    {tab.name}
                  </Tab>
                ))}
            </TabsDiv>
          </>
        }
      />
    ) : (
      <Content>
        <Outlet context={{ refresh, appliedFilters, search }} />
      </Content>
    )}

      {showCancelModal && (
        <ConfirmationModal
          isOpen={showCancelModal}
          title={
            isEditPage
              ? 'Cancelar Edição de Pedido'
              : 'Cancelar Criação de Pedido'
          }
          message='Você tem certeza? Todas as alterações não salvas serão perdidas.'
          actions={[
            { label: 'Confirmar', onClick: handleCancel, variant: 'danger' },
            {
              label: 'Voltar',
              onClick: () => setShowCancelModal(false),
              variant: 'outline',
            },
          ]}
        />
      )}
    </>
  )
}

const SteelOrders: React.FC = () => {
  return (
    <OrderActionsProvider>
      <SteelOrdersLayout />
    </OrderActionsProvider>
  )
}

export default SteelOrders
